import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";
import { Bell, CheckCheck, Inbox, Radio, AlarmClock, CalendarClock } from "lucide-react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";

const TYPE_COLOR = {
  ticket_assigned: "bg-indigo-50 text-indigo-700",
  ticket_comment: "bg-blue-50 text-blue-700",
  status_change: "bg-amber-50 text-amber-700",
  critical_ticket: "bg-rose-50 text-rose-700",
  inbound_ticket: "bg-emerald-50 text-emerald-700",
  user_invited: "bg-purple-50 text-purple-700",
  upcoming_callback: "bg-amber-50 text-amber-700",
  missed_callback: "bg-rose-50 text-rose-700",
};

const TYPE_LABEL = {
  ticket_assigned: "Assigned",
  ticket_comment: "New reply",
  status_change: "Status",
  critical_ticket: "Critical",
  inbound_ticket: "Inbound",
  user_invited: "Team",
  upcoming_callback: "CB due",
  missed_callback: "Missed CB",
};

const CALLBACK_TYPES = new Set(["upcoming_callback", "missed_callback"]);

function CallbackActions({ notif, onDone }) {
  const [showPicker, setShowPicker] = useState(false);
  const [when, setWhen] = useState("");
  const [busy, setBusy] = useState(false);
  const leadId = (notif.link || "").split("/leads/")[1];
  if (!leadId) return null;

  const act = async (payload, msg) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${leadId}/callback/snooze`, payload);
      toast.success(`${msg} — now due ${String(data.follow_up_at).slice(0, 16).replace("T", " ")}`);
      onDone(notif);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update callback");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-1.5" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
      <div className="flex items-center gap-1.5">
        <button
          disabled={busy}
          onClick={() => act({ minutes: 10 }, "Callback snoozed 10 min")}
          data-testid={`notif-snooze-${notif.id}`}
          className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-1 rounded border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:opacity-50 transition-colors"
        >
          <AlarmClock className="w-3 h-3" /> Snooze 10m
        </button>
        <button
          disabled={busy}
          onClick={() => setShowPicker((s) => !s)}
          data-testid={`notif-reschedule-${notif.id}`}
          className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-1 rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors"
        >
          <CalendarClock className="w-3 h-3" /> Reschedule
        </button>
      </div>
      {showPicker && (
        <div className="flex items-center gap-1.5 mt-1.5">
          <input
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            data-testid={`notif-reschedule-input-${notif.id}`}
            className="h-7 text-[11px] px-1.5 border border-slate-200 rounded bg-white text-slate-700"
          />
          <button
            disabled={busy || !when}
            onClick={() => act({ until: when }, "Callback rescheduled")}
            data-testid={`notif-reschedule-confirm-${notif.id}`}
            className="text-[10px] font-medium px-2 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            Set
          </button>
        </div>
      )}
    </div>
  );
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [live, setLive] = useState(false);

  const load = () =>
    api.get("/notifications").then((r) => {
      setItems(r.data.items || []);
      setUnread(r.data.unread_count || 0);
    });

  useEffect(() => {
    load();
    // Fallback poll (every 30s) — keeps the bell in sync if SSE drops.
    const i = setInterval(load, 30000);

    // Real-time SSE stream
    const token = localStorage.getItem("kippu_token");
    let es;
    if (token) {
      try {
        es = new EventSource(`${API}/notifications/stream?token=${encodeURIComponent(token)}`);
        es.onopen = () => setLive(true);
        es.onerror = () => setLive(false);
        es.onmessage = (ev) => {
          if (!ev?.data) return;
          try {
            const n = JSON.parse(ev.data);
            // Optimistic prepend — full refresh from server next time bell opens
            setItems((prev) => [n, ...prev].slice(0, 50));
            setUnread((u) => u + 1);
          } catch (e) { console.warn("SSE parse fail:", e?.message); }
        };
      } catch (e) { console.warn("SSE unavailable:", e?.message); }
    }
    return () => {
      clearInterval(i);
      if (es) es.close();
    };
  }, []);

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    load();
  };

  const markAllRead = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  const fmt = (iso) => {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notif-bell"
          className="relative h-9 w-9 grid place-items-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50"
        >
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <span
              data-testid="notif-badge"
              className="absolute -top-1 -right-1 h-4 min-w-4 px-1 grid place-items-center rounded-full bg-rose-500 text-white text-[10px] font-semibold"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end" data-testid="notif-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div>
            <div className="font-heading text-sm font-semibold flex items-center gap-1.5">
              Notifications
              {live && (
                <span
                  className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5"
                  data-testid="notif-live-badge"
                  title="Real-time updates active"
                >
                  <Radio className="w-2.5 h-2.5" /> live
                </span>
              )}
            </div>
            <div className="text-xs text-slate-500">{unread} unread</div>
          </div>
          {unread > 0 && (
            <button
              onClick={markAllRead}
              className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
              data-testid="notif-mark-all-read"
            >
              <CheckCheck className="w-3 h-3" />
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {items.length === 0 && (
            <div className="text-center py-12 text-slate-400 text-sm">
              <Inbox className="w-6 h-6 mx-auto mb-2 opacity-60" />
              You're all caught up
            </div>
          )}
          {items.map((n) => (
            <Link
              key={n.id}
              to={n.link || "#"}
              data-testid={`notif-item-${n.id}`}
              onClick={() => {
                setOpen(false);
                if (!n.read) markRead(n.id);
              }}
              className={`block px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                !n.read ? "bg-indigo-50/30" : ""
              }`}
            >
              <div className="flex items-start gap-3">
                <span
                  className={`inline-block text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${TYPE_COLOR[n.type] || "bg-slate-100 text-slate-700"}`}
                >
                  {TYPE_LABEL[n.type] || n.type}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{n.title}</div>
                  {n.body && (
                    <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.body}</div>
                  )}
                  {CALLBACK_TYPES.has(n.type) && (
                    <CallbackActions
                      notif={n}
                      onDone={(notif) => { if (!notif.read) markRead(notif.id); else load(); }}
                    />
                  )}
                  <div className="text-[10px] text-slate-400 mt-1">{fmt(n.created_at)}</div>
                </div>
                {!n.read && <span className="w-2 h-2 rounded-full bg-indigo-500 mt-1.5 shrink-0" />}
              </div>
            </Link>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
